import { Box, Flex } from '@chakra-ui/react';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, SearchInput } from '@morressier/alembic';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchWorkFlowDetails, fetchWorkspaceJournals } from 'src/api/queries';
import { useWorkspace } from '../Users/userHook';
import SubmissionLoader from '../../Submissions/SubmissionLoader';
import ScrollToTopButton from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/ScrollToTopButton';
import WorkFlowAccordion from './workFlowAccordion';
import JournalMenu, {
  Journal
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/JournalFilter';
import Footer from '../../../../../components/Footer';
import { WorkspaceNavLoader } from '../../../../../components/WorkspaceNav/ManageWorkspace';

const JOURNALS_PAGE_LIMIT = 10;

const WorkFlows = () => {
  const { shortname = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const [searchedValue, setSearchedValue] = useState('');
  const [workflowState, setWorkflowState] = useState({
    filters: {},
    subBucket: '',
    subBucketId: '',
    bucketId: '',
    isDynamic: false
  });

  const { workspaceId } = useWorkspace();

  /*** React Query: Fetch Journals (paginated) ***/
  const {
    data: journals,
    isLoading: isJournalsLoading,
    hasNextPage: hasMoreJournals,
    fetchNextPage: fetchMoreJournals,
    isFetchingNextPage: isFetchingMoreJournals
  } = useInfiniteQuery({
    // 'paginated' keeps this a separate cache entry from the plain
    // ['workspace-journals', { workspaceId }] query used elsewhere — sharing one
    // entry would publish this { pages, pageParams } shape to consumers expecting
    // { collection, total }. Appending rather than renaming still matches the
    // ['workspace-journals'] invalidations that follow a journal mutation.
    queryKey: ['workspace-journals', { workspaceId }, 'paginated'],
    queryFn: ({ pageParam = 0 }) =>
      workspaceId
        ? fetchWorkspaceJournals(workspaceId, pageParam, JOURNALS_PAGE_LIMIT)
        : null,
    enabled: !!workspaceId,
    retry: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;
      const nextOffset = allPages.length * JOURNALS_PAGE_LIMIT;
      return nextOffset < lastPage.total ? nextOffset : undefined;
    }
  });

  const journalOptions: Journal[] = useMemo(
    () =>
      journals?.pages?.flatMap(
        page =>
          page?.collection?.map(item => ({
            value: item.id,
            label: item.data.name
          })) ?? []
      ) ?? [],
    [journals]
  );

  /*** Default selected journal: first in the list or from URL params ***/
  const [appliedJournal, setAppliedJournal] = useState<Journal | undefined>();

  useEffect(() => {
    if (journalOptions.length === 0) return;

    // Check if journalId is present in URL params
    const queryParams = new URLSearchParams(location.search);
    const journalIdFromUrl = queryParams.get('journalId');

    if (journalIdFromUrl) {
      // Find the journal matching the journalId from URL
      const matchedJournal = journalOptions.find(
        journal => journal.value === journalIdFromUrl
      );
      if (matchedJournal) {
        setAppliedJournal(matchedJournal);
        return;
      }

      // The journal sits beyond the pages loaded so far, so keep paging
      // instead of falling back to the first journal.
      if (hasMoreJournals && !isFetchingMoreJournals) {
        fetchMoreJournals();
        return;
      }
    }

    // Fallback to first journal if no match found or no journalId in URL
    if (!appliedJournal) {
      setAppliedJournal(journalOptions[0]);
    }
  }, [
    journalOptions,
    appliedJournal,
    location.search,
    hasMoreJournals,
    isFetchingMoreJournals,
    fetchMoreJournals
  ]);

  /*** React Query: Fetch Workflows ***/
  const { isLoading, data, isError } = useQuery({
    queryKey: ['workflow-list', workspaceId, appliedJournal?.value],
    enabled: !!workspaceId && !!appliedJournal,
    queryFn: () => fetchWorkFlowDetails(appliedJournal!.value),
    retry: 0
  });

  /*** Sync workFlows state with URL query params ***/
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    Object.entries(workflowState.filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        queryParams.set(key, value.join(','));
      } else if (typeof value === 'string' && value.trim() !== '') {
        queryParams.set(key, value);
      } else {
        queryParams.delete(key);
      }
    });

    if (searchedValue !== '') {
      queryParams.set('searchParams', searchedValue);
    }
    if (workflowState.subBucket !== '') {
      queryParams.set('subBucketName', workflowState.subBucket);
    }
    if (workflowState.subBucketId !== '') {
      queryParams.set('subBucket', workflowState.subBucketId);
    }
    if (workflowState.bucketId !== '') {
      queryParams.set('bucket', workflowState.bucketId);
    }
    if (workflowState.isDynamic) {
      queryParams.set('isDynamic', workflowState.isDynamic.toString());
    }

    const newQueryString = queryParams.toString();
    const currentQueryString = new URLSearchParams(location.search).toString();

    if (newQueryString !== currentQueryString) {
      navigate(`/app/workspace/${shortname}/submissions?${newQueryString}`);
    }
  }, [searchedValue, workflowState, location.search, navigate, shortname]);

  const handleApplyJournal = (selected: Journal) => {
    setAppliedJournal(selected);
    // Update URL with the selected journalId
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('journalId', selected.value);
    navigate(`/app/workspace/${shortname}/workflow?${queryParams.toString()}`);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const journalId = queryParams.get('journalId')?.split(',') || [];
    if (journalId && journalId.length > 0) {
      const matchedJournal = journalOptions.find(
        journal => journal.value === journalId[0]
      );
      // The journal may live on a page that is not loaded yet, in which case
      // the effect above keeps paging — don't clear the selection here.
      if (matchedJournal) {
        setAppliedJournal(matchedJournal);
      }
    }
  }, [location.search]);

  /*** Handle empty journals safely ***/
  if (isJournalsLoading) {
    return <WorkspaceNavLoader size="normal" />;
  }

  if (journalOptions.length === 0) {
    return (
      <Flex height="100%" flexDirection="column" padding="5">
        <SubmissionLoader
          title="No Journals Found"
          subTitle="You do not have access to any journals in this workspace."
          bgColor="neutralSecondaryFill"
        />
      </Flex>
    );
  }

  return (
    <Box id="scrollable-wrapper" overflowY="auto" height="100%">
      <Box height="100%">
        <Box padding={{ base: 3, md: 5 }} minHeight="100%">
          <Flex
            flexDirection="column"
            width="100%"
            alignItems="flex-start"
            gap={2}
          >
            <Flex flexDirection="column" gap={2} width="100%">
              <Flex flexDirection="column" gap={2}>
                <Flex
                  align={{ base: 'stretch', lg: 'center' }}
                  justify="space-between"
                  flexDirection={{ base: 'column', lg: 'row' }}
                  gap={{ base: 3, lg: 2 }}
                  width="100%"
                  mb={2}
                >
                  <Box width={{ base: '100%', lg: 'auto' }}>
                    <JournalMenu
                      appliedJournal={appliedJournal!}
                      options={journalOptions}
                      onApply={handleApplyJournal}
                      hasNextPage={hasMoreJournals}
                      isLoadingMore={isFetchingMoreJournals}
                      onLoadMore={() => fetchMoreJournals()}
                    />
                  </Box>

                  {/* Right Side - Search + Filter */}
                  <Flex
                    align="center"
                    gap={3}
                    width={{ base: '100%', lg: 'auto' }}
                  >
                    <Flex gap={2} alignItems="center" flex="1">
                      <SearchInput
                        placeholder="Search submissions by title or manuscript ID"
                        iconLeft={<FontAwesomeIcon icon={faSearch} />}
                        width="100%"
                        maxWidth={{ base: '100%', lg: '450px' }}
                        name="searchParams"
                        value={searchValue}
                        enterKeyHint="search"
                        onChange={e => setSearchValue(e.currentTarget.value)}
                        onInputClear={() => setSearchValue('')}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            setSearchedValue(searchValue.trim());
                          }
                        }}
                        onKeyUp={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            setSearchedValue(searchValue.trim());
                          }
                        }}
                      />
                      <Box display={{ base: 'block', lg: 'none' }} flexShrink={0}>
                        <Button
                          variant="primary"
                          aria-label="Search"
                          onClick={() => setSearchedValue(searchValue.trim())}
                        >
                          <FontAwesomeIcon icon={faSearch} />
                        </Button>
                      </Box>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>

            <Box width="100%">
              {isLoading ? (
                <SubmissionLoader
                  title="Loading Search Results"
                  subTitle="We are retrieving your search results. This should only take a moment."
                  bgColor="neutralSecondaryFill"
                />
              ) : data?.buckets && data.buckets.length > 0 ? (
                <Box overflowY="auto" overflowX="auto" width="100%">
                  <WorkFlowAccordion
                    journalId={appliedJournal!.value}
                    workFlowGroups={data.buckets}
                    setWorkflowState={setWorkflowState}
                  />
                </Box>
              ) : isError ? (
                <SubmissionLoader
                  title="Search Failed"
                  subTitle="We encountered an issue connecting to the search service. Please check your connection or try again later."
                  bgColor="errorPrimaryFill"
                />
              ) : (
                <SubmissionLoader
                  title="No Results Found"
                  subTitle="We could not find any results matching your search criteria. Please adjust your search and try again."
                  bgColor="neutralSecondaryFill"
                />
              )}
            </Box>
          </Flex>
        </Box>
        <Footer />
      </Box>
      <ScrollToTopButton />
    </Box>
  );
};

export default WorkFlows;
